/** @jsx jsx */

import { Fragment as _, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import lizard from 'assets/Loader_Logo_2.gif'; // KeyImage.png'; // lizard.svg';
import { colors } from 'styles/variables';
import 'services/elementRemovePolyfill';

import { getCache } from 'services/imageCache';

const moduleImagesPreloaderDomContainerId = 'module-images-preloader';

// Image loading creates a new promise and wraps the DOM based
// image loading element. IF there is an error, we just mark it as resolved and
// move on, however we probably want to adjust this in the future to report on
// possible failures
const loadImage = (src, cb): Promise<any> => {
  return new Promise(resolve => {
    const img = new Image();
    img.src = src;

    img.onload = () => {
      cb(src);
      resolve(src);
    };

    img.onerror = () => {
      cb(src);
      resolve(src);
    };
  });
};

const ModuleImagesPreloader = ({ images }) => {
  const [imgsLoaded, setImgsLoaded] = useState(false);
  const [whichImagesLoaded, setImgAsLoaded] = useState<String[]>([]);
  const [minDisplayTime, setMinDisplayTime] = useState(false);
  const [boot, setBoot] = useState(false);

  const preLoader = useRef<any>(null);
  const documentTitle = useRef<string>(document.title);

  // let moduleImagesPreloaderDomContainer = document.querySelector<
  //   HTMLDivElement
  // >(`#${moduleImagesPreloaderDomContainerId}`);

  const moduleImagesPreloaderDomContainer = useRef(
    document.querySelector<HTMLDivElement>(
      `#${moduleImagesPreloaderDomContainerId}`
    )
  );

  if (!boot && !moduleImagesPreloaderDomContainer.current) {
    moduleImagesPreloaderDomContainer.current = document.createElement(
      'div'
    ) as HTMLDivElement;

    moduleImagesPreloaderDomContainer.current.id = moduleImagesPreloaderDomContainerId;

    moduleImagesPreloaderDomContainer.current.style.position = 'fixed';
    moduleImagesPreloaderDomContainer.current.style.left = '0px';
    moduleImagesPreloaderDomContainer.current.style.top = '0px';
    moduleImagesPreloaderDomContainer.current.style.width = '100vw';
    moduleImagesPreloaderDomContainer.current.style.height = '100vh';
    moduleImagesPreloaderDomContainer.current.style.zIndex = '2147483647';
    //
    document.body.appendChild(moduleImagesPreloaderDomContainer.current);
  }

  useEffect(() => {
    const root = document.querySelector<HTMLDivElement>('#root');

    if (!boot && root) {
      document.title = 'Module loading';
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('tabindex', '-1');

      return;
    }

    document.title = documentTitle.current;
    root?.setAttribute('aria-hidden', 'false');
    root?.setAttribute('tabindex', '0');

    const sections = document.querySelectorAll('section') as NodeListOf<
      HTMLElement
    >;

    if (sections && sections[0]) {
      // might need to do something here if the page jumps to top
      // from giving first interaction focus
      //

      if (!window.location.hash) {
        sections[0].focus();
      }
    }

    return () => {
      // set the focus back to root ?
      // is doing it above. double tap it maybe ?
    };
  }, [boot]);

  // Show the loader for at least 1000 ms
  useEffect(() => {
    setTimeout(() => {
      !minDisplayTime && setMinDisplayTime(true);
    }, 1000);
  }, []);

  useEffect(() => {
    if (!imgsLoaded) {
      console.time('TIME TO IMAGES LOADED');
    } else {
      console.timeEnd('TIME TO IMAGES LOADED');
    }
  }, [imgsLoaded]);

  useEffect(() => {
    whichImagesLoaded.length === images.length && setImgsLoaded(true);
  }, [whichImagesLoaded]);

  // Preload Images on load
  useEffect(() => {
    const fun = async () => {
      const cacheAvailable = 'caches' in self;

      if (!cacheAvailable) {
        images.map((imgSrc: string) => {
          return loadImage(imgSrc, () => {
            setImgAsLoaded(whichImagesLoaded => [...whichImagesLoaded, imgSrc]);
          });
        });

        return;
      }

      const cache = await getCache();

      images.map(async (imgSrc: string) => {
        const resp = await cache.match(imgSrc);

        if (!resp) {
          try {
            await cache.add(imgSrc);
          } catch (err) {
            console.log(err);
          }
        }

        setImgAsLoaded(whichImagesLoaded => [...whichImagesLoaded, imgSrc]);
      });

      return;
    };

    fun();
  }, []);

  // Fade out the loading screen after the fade out is completed,
  // remove the loading screen from the DOM
  useEffect(() => {
    if (!preLoader.current) {
      return;
    }

    const fader = () => {
      setBoot(true);
    };

    preLoader.current?.addEventListener('transitionend', fader);

    return () => {
      preLoader.current?.removeEventListener('transitionend', fader);
    };
  }, []);

  useEffect(() => {
    // do the polyfill for ie ?

    if (boot) {
      moduleImagesPreloaderDomContainer.current?.remove();
    }
  }, [boot]);

  const percentLoaded = (whichImagesLoaded.length / images.length) * 100;

  if (boot || !moduleImagesPreloaderDomContainer.current) {
    return null;
  }

  return createPortal(
    <div
      ref={preLoader}
      css={css`
        position: fixed;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        left: 0;
        top: 0;
        width: 100vw;
        height: 100vh;
        background: #fff;
        opacity: ${imgsLoaded ? 0 : 1};
        transition: opacity 0.35s;
        z-index: 2147483638;
      `}
    >
      <img
        src={lizard}
        alt=""
        css={css`
          max-width: 265px;
        `}
      />

      <div
        css={css({
          width: 250,
          margin: '-15px 0 0 0',
          fontFamily: 'Poppins, Sans serif',
          fontWeight: 200,
          fontSize: 22,
        })}
      >
        <div
          css={css({
            textAlign: 'center',
            textTransform: 'capitalize',
          })}
          tabIndex={0}
          aria-label="Module loading"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percentLoaded)}
          role="progressbar"
        >
          Module loading
          {/* <span className="sr-only" aria-live="polite">
            {Math.round(percentLoaded).toString()}%
          </span> */}
        </div>

        <div
          css={css({
            width: '100%',
            height: 15,
            margin: '30px 0 0 0',
            borderRadius: 9999,
            overflow: 'hidden',
            background: '#b3f4f2',
          })}
        >
          <div
            style={{
              width: `${percentLoaded}%`,
              height: 'inherit',
              borderRadius: 9999,
              background: colors.lTeal1,
            }}
          />
        </div>
      </div>
    </div>,
    moduleImagesPreloaderDomContainer.current
  );
};

export default ModuleImagesPreloader;
